import { useMemo, useState } from 'react'
import { useParams } from 'react-router'
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
  useComboboxAnchor,
} from 'tailwind-v4/components/combobox'
import { UserAvatar } from 'tailwind-v4/components/user-avatar'
import { useDebounce } from '@/hooks/useDebounce'
import { defaultServicePagination } from '@/connect-types/backend/service'
import { customerApi } from '@/pages/[org_id]/customer/[id]/layout'
import { gravatarImage } from '@/utils/common'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export interface EmailContactValue {
  email: string
  name: string
}

interface PassedProps {
  /** Selected recipients. Fully controlled. */
  value: EmailContactValue[]
  onChange: (next: EmailContactValue[]) => void
  /** Scopes the customer search to one venue. Falls back to the route param. */
  serial?: string
  id?: string
  invalid?: boolean
  placeholder?: string
}

function contactLabel(contact: EmailContactValue) {
  return contact.name ? `${contact.name} <${contact.email}>` : contact.email
}

/**
 * Recipient picker: searches the org's customers as you type and renders each
 * pick as a removable chip. A valid address that matches no customer is
 * offered as a free-text option, so a one-off recipient still takes one Enter.
 *
 * Identity is the email, not the object: the selected values come from the
 * form while the options come from the search, so they never match by
 * reference (`isItemEqualToValue`).
 */
export function EmailContacts({
  value,
  onChange,
  serial: serialProp,
  id,
  invalid,
  placeholder = 'Search customers or type an email…',
}: PassedProps) {
  const { serial: serialParam = '', org_id: orgId = '' } = useParams<{
    serial: string
    org_id: string
  }>()
  const serial = serialProp ?? serialParam

  const anchorRef = useComboboxAnchor()
  const [query, setQuery] = useState('')
  const search = useDebounce(query, 400)

  const { data: customers = defaultServicePagination } =
    customerApi.useGetCustomersQuery({
      orgId,
      search,
      query: { serial, limit: 5, cursor: '', tag_id: '' },
    })

  const items = useMemo<EmailContactValue[]>(() => {
    const picked = new Set(value.map((contact) => contact.email.toLowerCase()))

    const found = customers.data
      .map((customer) => ({
        email: customer?.user_profile.email ?? '',
        name: [customer?.user_profile.first, customer?.user_profile.last]
          .filter(Boolean)
          .join(' '),
      }))
      .filter(
        (contact) => contact.email && !picked.has(contact.email.toLowerCase())
      )

    const typed = query.trim()
    const isNewAddress =
      EMAIL_RE.test(typed) &&
      !picked.has(typed.toLowerCase()) &&
      !found.some(
        (contact) => contact.email.toLowerCase() === typed.toLowerCase()
      )

    return isNewAddress ? [{ email: typed, name: '' }, ...found] : found
  }, [customers.data, query, value])

  return (
    <Combobox<EmailContactValue, true>
      items={items}
      multiple
      autoHighlight
      // The search runs on the server, so base-ui must not filter the results
      // it is handed a second time against the raw input.
      filter={null}
      value={value}
      inputValue={query}
      onInputValueChange={(next) => {
        setQuery(next)
      }}
      itemToStringLabel={contactLabel}
      isItemEqualToValue={(a, b) =>
        a.email.toLowerCase() === b.email.toLowerCase()
      }
      onValueChange={(next) => {
        onChange(next)
        setQuery('')
      }}
    >
      <ComboboxChips ref={anchorRef} className="min-h-10">
        <ComboboxValue>
          {(selected: EmailContactValue[]) =>
            selected.map((contact) => (
              <ComboboxChip key={contact.email} aria-label={contact.email}>
                {contact.email}
              </ComboboxChip>
            ))
          }
        </ComboboxValue>
        <ComboboxChipsInput
          id={id}
          aria-invalid={invalid}
          placeholder={value.length > 0 ? undefined : placeholder}
        />
      </ComboboxChips>
      <ComboboxContent anchor={anchorRef}>
        <ComboboxEmpty>No matching customers</ComboboxEmpty>
        <ComboboxList>
          {(contact: EmailContactValue) => (
            <ComboboxItem key={contact.email} value={contact}>
              <UserAvatar
                size="sm"
                user={{
                  first_name: contact.name.split(' ')[0] ?? '',
                  last_name: contact.name.split(' ').slice(1).join(' '),
                  email: contact.email,
                }}
                resolveAvatarUrl={(email) =>
                  gravatarImage(email, 'tiny') || null
                }
              />
              <span className="min-w-0 flex-1 truncate">
                {contact.name ? (
                  <>
                    {contact.name}{' '}
                    <span className="text-muted-foreground">
                      {contact.email}
                    </span>
                  </>
                ) : (
                  contact.email
                )}
              </span>
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}

export default EmailContacts
